'use client';

import type { ClassValue } from 'clsx';
import { Option } from 'effect';

import NavbarCartActive from './NavbarCartActive';
import NavbarCartFallback from './NavbarCartFallback';

import { pendingCartLinesAtom } from '../../../../effect/atom/order/cartMutations';
import { cartState, knownCartAtom } from '../../../../effect/bind/order';
import { useAtomValue } from '@effect/atom-react';
import {
  hasLiveCartWrite,
  hasUncertainCartWrite,
} from '@vs-shop/engine/module/cart/cartDrawerProjection';

type Props = {
  className?: ClassValue;
};

/**
 * The basket affordance, over the last cart the backend confirmed plus the
 * lines still on their way to it. The count moves on the click, not on the
 * response.
 *
 * The DTO stops here: the shared affordance takes a count, and every branch
 * that cannot produce one — nothing loaded, no order, an order already placed,
 * with nothing pending — renders the same affordance without a badge.
 */
export default function NavbarCart({ className }: Props) {
  const knownCart = useAtomValue(knownCartAtom);
  const itemsCount = useAtomValue(cartState.projectedUnitCountAtom);
  const pendingLines = useAtomValue(pendingCartLinesAtom);
  const isNavigationBlocked = hasLiveCartWrite(pendingLines);
  const isNavigationUncertain = hasUncertainCartWrite(pendingLines);

  return itemsCount > 0 || Option.isSome(knownCart) ? (
    <NavbarCartActive
      className={className}
      itemsCount={itemsCount}
      isNavigationBlocked={isNavigationBlocked}
      isNavigationUncertain={isNavigationUncertain}
    />
  ) : (
    <NavbarCartFallback
      className={className}
      isNavigationBlocked={isNavigationBlocked}
      isNavigationUncertain={isNavigationUncertain}
    />
  );
}
